import { useUniversalContext } from '@cxc/react-universal-components'
import {
  CustomerInfoFormInput,
  CustomerInfoFormKeys,
} from './customerInfoValidation'
import { useCallback, useEffect, useState } from 'react'
import { UseFormTrigger } from 'react-hook-form/dist/types/form'
import { DropDownValue, stateProvinceMap } from '../../utils/constants'
import { logger } from '../../utils/logger'
import { trimFormattedUSPhone } from '../../utils/formatting'
import { useLocale } from '../../hooks/useLocale'
import { CountryCode } from '../../types/locale'
import { LoadingState } from '../../types/general'

interface CompanyInfo {
  companyName?: string
  companyBillingAddress1?: string
  companyBillingAddress2?: string
  companyBillingCity?: string
  companyBillingState?: string
  companyBillingCountry?: {
    code?: string
  }
  companyBillingZipCode?: string
}

export function useUserInfo(
  setValue: (key: CustomerInfoFormKeys, value: string) => void,
  trigger: UseFormTrigger<CustomerInfoFormInput>,
) {
  const [loading, setLoading] = useState<LoadingState>('loading')
  const { isLoggedIn, user } = useUniversalContext()

  const { countryCode } = useLocale()

  const setField = useCallback(
    (key: CustomerInfoFormKeys, value?: string) => {
      if (value) {
        setValue(key, value)
      }
    },
    [setValue],
  )

  useEffect(() => {
    if (isLoggedIn && user) {
      setField('firstName', user.firstName)
      setField('lastName', user.lastName)
      setField('email', user.email)
      setField(
        'phoneNumber.value',
        getFormattedPhone(user.primaryPhone as string | undefined),
      )

      if (user?.companyInfo) {
        const companyInfo = user.companyInfo as CompanyInfo
        setField('companyLegalName', companyInfo.companyName)
        setField('streetAddress1', companyInfo.companyBillingAddress1)
        setField('streetAddress2', companyInfo.companyBillingAddress2)
        setField('city', companyInfo.companyBillingCity)

        const stateProvince = getBillingStateProvince(
          companyInfo.companyBillingState,
          countryCode,
        )
        if (stateProvince) {
          setField('stateProvince.value', stateProvince.value)
          setField('stateProvince.label', stateProvince.label)
        }
        if (companyInfo?.companyBillingZipCode) {
          setValue('postalCode', companyInfo.companyBillingZipCode)
        }
      }

      trigger().catch((error) => {
        logger('warn', 'Error triggering validation', error)
      })
    }
    setLoading('success')
  }, [countryCode, isLoggedIn, setValue, trigger, user, setField])

  return { loading }
}

function getFormattedPhone(primaryPhone: string | undefined) {
  return primaryPhone ? trimFormattedUSPhone(primaryPhone) : undefined
}

function getBillingStateProvince(
  billingState: string | undefined,
  countryCode: CountryCode,
): DropDownValue<string> | undefined {
  if (billingState) {
    const formattedState = billingState.replace(/\s+/g, '')

    const stateProvinceOptions = stateProvinceMap[countryCode]
    return stateProvinceOptions.find((state) => state.value === formattedState)
  }
}
